'use client';

import { useRef } from 'react';
import CountUp from 'react-countup';
import { ArrowRight, Info, User } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export default function StatisticsConveyor() {
  const ref = useRef<HTMLDivElement>(null);

  // Generate array for person visualization (100 items)
  const personIcons = Array.from({ length: 100 }).map((_, index) => {
    // The first 18 people are employed (18%)
    return index < 18;
  });

  return (
    <section id="statistics" className="py-20 bg-ivory relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-grid-pattern" />
      </div>
      
      <div className="container-custom">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <div
          >
            <h2 className="heading-2 text-oxford mb-4">
              The Education-Employment Gap
            </h2>
            <p className="body-text text-oxford/80">
              In India, while 98% of children enroll in primary school, only 33% pursue higher education, 
              and merely 55% of these graduates are considered employable.
            </p>
          </div>
        </div>

        <div ref={ref} className="relative">
          {/* People Infographic Visualization */}
          <div
            className="mt-12 mb-16 max-w-4xl mx-auto"
          >
            <div className="p-8 bg-white rounded-lg shadow-sm border border-oxford/5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="flex flex-col justify-center">
                  <div className="grid grid-cols-10 gap-2">
                    {personIcons.map((isEmployed, index) => (
                      <div 
                        key={index} 
                        className="flex justify-center"
                        style={{ 
                          transition: `opacity 0.5s ease ${0.01 * index}s`
                        }}
                      >
                        <User 
                          className={`size-6 ${isEmployed ? 'text-dark-peach' : 'text-oxford/20'}`} 
                          fill={isEmployed ? 'currentColor' : 'none'}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col justify-center space-y-4">
                  <h3 className="text-5xl font-heading text-oxford">
                    <span className="text-dark-peach">18</span> out of <span className="text-oxford">100</span>
                  </h3>
                  <p className="text-oxford/80">
                    For every 100 children who start primary school in India, 
                    only 18 secure meaningful employment by the end of their 
                    educational journey.
                  </p>
                  <p className="text-xs text-oxford/60 italic">
                    Source: India Skill Report, World Bank Data
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* CTA Section */}
          <div
            className="mt-16 text-center"
          >
            <p className="text-oxford/80 max-w-xl mx-auto mb-6">
              We're working to change these statistics through holistic education and employment pathways.
            </p>
            <a 
              href="/what-we-do" 
              className="px-6 py-3 bg-mid-blue text-ivory rounded-md inline-flex items-center gap-2 hover:bg-mid-blue/90 transition-colors shadow-md"
            >
              See Our Approach
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
      
      {/* Add background grid pattern CSS */}
      <style jsx>{`
        .bg-grid-pattern {
          background-image: linear-gradient(to right, rgba(9, 31, 53, 0.05) 1px, transparent 1px),
                           linear-gradient(to bottom, rgba(9, 31, 53, 0.05) 1px, transparent 1px);
          background-size: 40px 40px;
        }
      `}</style>
    </section>
  );
} 